/**
 * Storage containers for Foundation.Storable instances.
 * @constructor
 * @param {string} namespace Namespace this manager object will be responsible for.
 * @param {Foundation.Storable.Bucket.Persistence=} persistence How long should data in this bucket be kept.
 */
Foundation.Storable.Bucket = function (namespace, persistence) {
    /**
     * @private
     * @type {string}
     */
    this._namespace = namespace;

    if (namespace.match(/[^\w.]/)) {
        throw new Foundation.Storable.Bucket.Error(
            this,
            "Namespaces must be alphanumeric with optional dots."
        );
    }

    /**
     * @private
     * @type {Foundation.Storable.Bucket.Persistence}
     */
    this._persistence = persistence || Foundation.Storable.Bucket.Persistence.INDEFINITE;

    /**
     * @private
     * @type {Storage|Foundation.Storable.WebStorageEmulator}
     */
    this._storageObject = null;

    /**
     * @private
     * @type {RegExp}
     */
    this._keyPrefixRe = new RegExp(
        `^F\\.S\\.B\\.${this._namespace.replace(/\./, "\\.")}\\.`
    );

    /**
     * @private
     * @type {number}
     */
    this._bucketInstanceId = (new Date()).getTime();

    var storageObjFactory = Foundation.Storable.WebStorageAbstractFactory.sharedInstance();
    if (this._persistence === Foundation.Storable.Bucket.Persistence.INDEFINITE) {
        this._storageObject = storageObjFactory.localStorageObject();
    }
    else if (this._persistence === Foundation.Storable.Bucket.Persistence.SESSION) {
        this._storageObject = storageObjFactory.sessionStorageObject();
    }
};

/**
 * Type of persistent storage.
 * @enum {number}
 */
Foundation.Storable.Bucket.Persistence = {
    _UNSPECIFIED: 0,
    SESSION: 1,
    INDEFINITE: 2
};

/**
 * Exceptions thrown by buckets.
 * @constructor
 * @extends {Error}
 * @param {Foundation.Storable.Bucket} bucket
 * @param {string} message
 */
Foundation.Storable.Bucket.Error = function (bucket, message) {
    var fsb = "Foundation.Storable.Bucket";
    Error.call(this, `${fsb}: ${bucket.namespace()}: ${message}`);
};
Foundation.inherit(Foundation.Storable.Bucket.Error, Error);

/**
 * Tests a string to see if it is safe for use as a storable identifier.
 * @param {string} identifier
 * @return {boolean}
 */
Foundation.Storable.Bucket.identifierIsUnsafe = function (identifier) {
    return !!(identifier.match(/[^A-Za-z0-9_]/))
}

/**
 * Return a Foundation.Storable object from the bucket if it exists,
 * or a new instance with the given identifier if it doesn't.
 * @param {string} identifier
 * @return {Foundation.Storable}
 */
Foundation.Storable.Bucket.prototype.fetchStorable = function (identifier) {
    return this.getStorable(identifier) || this._newStorable(identifier);
}

/**
 * Retrive a Foundation.Storable from this bucket.
 * @param {string} identifier
 * @return {Foundation.Storable}
 */
Foundation.Storable.Bucket.prototype.getStorable = function (identifier) {
    var jsonString = this._getString(identifier);

    if (!jsonString) {
        return null;
    }

    var storableObj = this.deserializeStorableForIdentifier(jsonString, identifier);
    return storableObj;
}

/**
 * Initializes a new Foundation.Storable instance with the given identifier
 * and the default save-bucket set.
 * @private
 * @param {string} identifier
 * @return {Foundation.Storable}
 */
Foundation.Storable.Bucket.prototype._newStorable = function (identifier) {
    return (new Foundation.Storable(identifier))
        .setDefaultBucket(this)
        .setBucketInstanceId(this._bucketInstanceId);
}

/**
 * @param {string} jsonString
 * @param {string} identifier
 * @return {Foundation.Storable}
 */
Foundation.Storable.Bucket.prototype.deserializeStorableForIdentifier = function (jsonString, identifier) {
    var wrapperObj = this._deserializeJSONStringForIdentifier(jsonString, identifier);
    var storableObj = this._newStorable(identifier);

    if (wrapperObj["v"] === 1) {
        storableObj.setLastSaveTime(wrapperObj["t"]);
        storableObj.setBucketInstanceId(wrapperObj["b"] || this._bucketInstanceId);
        storableObj.extend(wrapperObj["d"]);
    }
    else {
        throw new Foundation.Storable.Bucket.Error(
            this,
            `Can't parse unknown version: ${wrapperObj["v"]}`
        );
    }

    return storableObj;
}

/**
 * @param {Foundation.Storable} storable
 * @return {string}
 */
Foundation.Storable.Bucket.prototype.serializeStorable = function (storable) {
    return JSON.stringify({
        "v": 1,
        "t": storable.lastSaveTime(),
        "b": this._bucketInstanceId,
        "d": storable.storableData()
    });
}

/**
 * Save a Foundation.Storable in the bucket.
 * @public
 * @param {Foundation.Storable} storable
 * @return {Foundation.Promise}
 */
Foundation.Storable.Bucket.prototype.saveStorable = function (storable) {
    var seconds = Math.floor(Foundation.currentTime() / 1000);
    storable.setLastSaveTime(seconds);
    storable.setBucketInstanceId(this._bucketInstanceId);
    var jsonString = this.serializeStorable(storable);
    return this._saveString(storable.storableIdentifier(), jsonString);
};

/**
 * @param {string} jsonString
 * @return {*}
 */
Foundation.Storable.Bucket.prototype._deserializeJSONStringForIdentifier = function (jsonString, identifier) {
    var obj = null;

    try {
        if (jsonString !== null) {
            obj = JSON.parse(jsonString);
        }
    }
    catch (e) {
        throw new Foundation.Storable.Bucket.Error(
            this,
            `Failed to parse JSON string for '${identifier}': ${e.message}`
        );
    }

    return obj;
}

/**
 * Namespace a key to avoid collisions.
 * @private
 * @return {string}
 */
Foundation.Storable.Bucket.prototype.namespacedKey = function (theKey) {
    if (Foundation.Storable.Bucket.identifierIsUnsafe(theKey)) {
        throw new Foundation.Storable.Bucket.Error(
            this,
            `Attempted to create an unsafe Foundation.Storable identifier: ${theKey}`)
    }

    return `F.S.B.${this._namespace}.${theKey}`;
}

/**
 * Namespace for the bucket.
 * @return {string}
 */
Foundation.Storable.Bucket.prototype.namespace = function () {
    return this._namespace;
}

/**
 * @private
 * @param {string} theKey
 * @param {string} theString
 * @return {Foundation.Promise}
 */
Foundation.Storable.Bucket.prototype._saveString = function (theKey, theString) {
    var promise = new Foundation.Promise();

    // Keep the old value around in order to mimic storage event.
    var oldValue = this._getString(theKey);

    // Replace key with namespaced version.
    theKey = this.namespacedKey(theKey);

    Foundation.Timer.deferral().then(function () {
        try {
            this._storageObject.setItem(theKey, theString);
            promise.fulfill();
        }
        catch (err) {
            Foundation.warn("Foundation.Storable.Bucket: ", err);
            promise.failWith({
                "errors": [err.message]
            })
        }
    }.bind(this));

    promise.then(function () {
        var dispatcher = Foundation.Storable.StorageEventDispatcher.sharedInstance();
        dispatcher.mimicStorageEvent(theKey, oldValue, theString);
    }.bind(this));

    return promise;
}

/**
 * @private
 * @param {string} theKey
 * @return {string?}
 */
Foundation.Storable.Bucket.prototype._getString = function (theKey) {
    // Replace key with namespaced version.
    theKey = this.namespacedKey(theKey);

    return /** @type {string?} */ (this._storageObject.getItem(theKey));
}

/**
 * @private
 * @return {Array.<string>}
 */
Foundation.Storable.Bucket.prototype.keys = function () {
    var keys = [];

    var len = this._storageObject.length;
    for (var i = 0; i < len; i++) {
        var key = this._storageObject.key(i);
        if (key && key.match(this._keyPrefixRe)) {
            keys.push(key.replace(this._keyPrefixRe, ""));
        }
    }

    return keys;
}

/**
 * Removes the given storable from the bucket.
 * @param {string} identifier
 * @return {Foundation.Promise}
 */
Foundation.Storable.Bucket.prototype.deleteStorable = function (identifier) {
    var promise = new Foundation.Promise();

    Foundation.Timer.deferral().then(function () {
        this._deleteStorableSync(identifier);
        promise.fulfill();
    }.bind(this));

    return promise;
}

Foundation.Storable.Bucket.prototype._deleteStorableSync = function (identifier) {
    var key = this.namespacedKey(identifier);
    var oldValue = this._getString(identifier);
    this._storageObject.removeItem(key);
    var dispatcher = Foundation.Storable.StorageEventDispatcher.sharedInstance();
    dispatcher.mimicStorageEvent(key, oldValue, null);
}

/**
 * @return {Foundation.Promise}
 */
Foundation.Storable.Bucket.prototype.clear = function () {
    var promise = new Foundation.Promise();

    Foundation.Timer.deferral().then(function () {
        this.keys().forEach(function (identifier) {
            this._deleteStorableSync(identifier)
        }.bind(this));
        promise.fulfill();
    }.bind(this));

    return promise;
}

/**
 * Watches for changes made to the identifier.
 * @param {string} identifier
 * @param {function(Foundation.Storable.Bucket.ChangeEvent)} callback
 */
Foundation.Storable.Bucket.prototype.observeChangesForIdentifier = function (identifier, callback) {
    if (Foundation.Storable.Bucket.identifierIsUnsafe(identifier)) {
        throw new Foundation.Storable.Bucket.Error(
            this,
            `Attempted to observe changes for an unsafe identifier: ${identifier}`
        )
    }

    var dispatcher = Foundation.Storable.StorageEventDispatcher.sharedInstance()
    var keyMatcherRe = new RegExp(`${this._keyPrefixRe.source + identifier}$`)
    dispatcher.addListenerForKeysMatching(keyMatcherRe, function (key, oldVal, newVal) {
        callback(new Foundation.Storable.Bucket.ChangeEvent(this, identifier, oldVal, newVal))
    }.bind(this))
}

/**
 * Removes all change observers for the given identifier.
 * @param {string} identifier
 */
Foundation.Storable.Bucket.prototype.removeChangeObserversForIdentifier = function (identifier) {
    if (Foundation.Storable.Bucket.identifierIsUnsafe(identifier)) {
        throw new Foundation.Storable.Bucket.Error(
            this,
            `Attempted to remove change observer for an unsafe identifier: ${identifier}`
        )
    }
    var dispatcher = Foundation.Storable.StorageEventDispatcher.sharedInstance()
    dispatcher.removeListenersForKey(this.namespacedKey(identifier))
}

/**
 * Removes all the change observers from this bucket.
 */
Foundation.Storable.Bucket.prototype.removeAllChangeObservers = function () {
    this.keys().forEach(function (identifier) {
        this.removeChangeObserversForIdentifier(identifier)
    }.bind(this))
}


/**
 * @constructor
 * @private
 * @param {Foundation.Storable.Bucket} target
 * @param {string} identifier
 * @param {string} oldSerializedStorable
 * @param {string} newSerializedStorable
 */
Foundation.Storable.Bucket.ChangeEvent = function (target, identifier, oldSerializedStorable, newSerializedStorable) {
    this._target = target
    this._identifier = identifier
    this._oldSerializedStorable = oldSerializedStorable
    this._newSerializedStorable = newSerializedStorable
}

/**
 * @public
 * @return {string}
 */
Foundation.Storable.Bucket.ChangeEvent.prototype.identifier = function () {
    return this._identifier;
}

/**
 * @public
 * @return {Foundation.Storable.Bucket}
 */
Foundation.Storable.Bucket.ChangeEvent.prototype.targetBucket = function () {
    return this._target;
}

/**
 * @public
 * @return {Foundation.Storable?}
 */
Foundation.Storable.Bucket.ChangeEvent.prototype.oldStorable = function () {
    return this._returnDeserializedStorable(this._oldSerializedStorable);
}

/**
 * @public
 * @return {Foundation.Storable?}
 */
Foundation.Storable.Bucket.ChangeEvent.prototype.newStorable = function () {
    return this._returnDeserializedStorable(this._newSerializedStorable);
}

/**
 * @private
 * @param {string?} jsonString
 * @return {Foundation.Storable}
 */
Foundation.Storable.Bucket.ChangeEvent.prototype._returnDeserializedStorable = function (jsonString) {
    if (jsonString === null) return null
    return this.targetBucket().deserializeStorableForIdentifier(jsonString, this.identifier())
}

/**
 * Important warning about this function: it only works for changes that are
 * not deletions of storables from a bucket. Calling this method on a deletion
 * event will always return false and also emit a warning message.
 * @public
 * @return {boolean}
 */
Foundation.Storable.Bucket.ChangeEvent.prototype.originatedFromOtherWindow = function () {
    var newStorable = this.newStorable()
    if (newStorable) {
        return (newStorable.bucketInstanceId() !== this.targetBucket()._bucketInstanceId)
    }
    else {
        Foundation.warn("Called originatedFromOtherWindow on deletion event, validity of return value is unknown.")
        return false
    }
}


Foundation.Storable.Bucket.prototype["namespace"] = Foundation.Storable.Bucket.prototype.namespace;
Foundation.Storable.Bucket.prototype["fetchStorable"] = Foundation.Storable.Bucket.prototype.fetchStorable;
Foundation.Storable.Bucket.prototype["getStorable"] = Foundation.Storable.Bucket.prototype.getStorable;
Foundation.Storable.Bucket.prototype["saveStorable"] = Foundation.Storable.Bucket.prototype.saveStorable;
Foundation.Storable.Bucket.prototype["keys"] = Foundation.Storable.Bucket.prototype.keys;
Foundation.Storable.Bucket.prototype["clear"] = Foundation.Storable.Bucket.prototype.clear;
Foundation.Storable.Bucket.prototype["observeChangesForIdentifier"] = Foundation.Storable.Bucket.prototype.observeChangesForIdentifier;
Foundation.Storable.Bucket.prototype["removeChangeObserversForIdentifier"] = Foundation.Storable.Bucket.prototype.removeChangeObserversForIdentifier;

Foundation.Storable.Bucket.ChangeEvent.prototype["identifier"] = Foundation.Storable.Bucket.ChangeEvent.prototype.identifier;
Foundation.Storable.Bucket.ChangeEvent.prototype["targetBucket"] = Foundation.Storable.Bucket.ChangeEvent.prototype.targetBucket;
Foundation.Storable.Bucket.ChangeEvent.prototype["oldStorable"] = Foundation.Storable.Bucket.ChangeEvent.prototype.oldStorable;
Foundation.Storable.Bucket.ChangeEvent.prototype["newStorable"] = Foundation.Storable.Bucket.ChangeEvent.prototype.newStorable;
Foundation.Storable.Bucket.ChangeEvent.prototype["originatedFromOtherWindow"] = Foundation.Storable.Bucket.ChangeEvent.prototype.originatedFromOtherWindow;
